import { useContext, useEffect, useRef, useState } from "react";
import { PlusCircle } from "react-feather";
import DataTable from "../Generic/DataTable";
import Dropdown from "../Generic/DropDown";
import Loading from "../Generic/loading";
import NumberBox from "../Generic/NumberBox";
import TextBox from "../Generic/TextBox";
import { tenantContext } from "../Main/contexts";
import {
    DeleteOrganization,
    DeleteOrganizationSecret,
    GetOrganization,
    GetOrganizations,
    MAIL_PROVIDERS,
    OrganizationItem,
    PutOrganization,
    SECRET_NAMES,
    SecretName,
} from "./ConfigAPI";
import {
    AUTHENTICATION_OPTIONS,
    EMPTY_SECRETS,
    FLAG_OPTIONS,
    FieldErrors,
    FormState,
    ORGANIZATION_FIELDS,
    Option,
    SecretInputs,
    choiceOptions,
    codeProblem,
    credentialsOptional,
    deleteConfirmText,
    displayName,
    errorSummary,
    failureMessage,
    fieldErrorsFromResponse,
    formFromSettings,
    formatModified,
    matchesText,
    organizationRequest,
    providerShows,
    secretPlaceholder,
} from "./configForm";
import EffectiveValue from "./EffectiveValue";
import { EDITOR_TITLE_STYLE, HEADER_BUTTON_STYLE, Section, column } from "./layout";
import "./styles.css";

type Direction = "inbound" | "outbound";

const LABELS: Record<string, string> = {
    code: "Code",
    name: "Name",
    enableCloudMailProcessing: "Enable cloud mail processing",
    autoResponseMaxPerWindow: "Max per window",
    autoResponseWindowMinutes: "Window minutes",
    autoResponseBodySizeToleranceBytes: "Body-size tolerance bytes",
};
for (const direction of ["inbound", "outbound"] as Direction[]) {
    LABELS[`${direction}.mailProvider`] = "Provider";
    LABELS[`${direction}.server`] = "Server";
    LABELS[`${direction}.port`] = "Port";
    LABELS[`${direction}.username`] = "Username";
    LABELS[`${direction}.msalTenantId`] = "MSAL tenant ID";
    LABELS[`${direction}.msalClientId`] = "MSAL client ID";
    LABELS[`${direction}Password`] = "Password";
    LABELS[`${direction}MsalClientSecret`] = "MSAL client secret";
}
LABELS["outbound.authenticationMethods"] = "Authentication";

/** For the alert, where "Port" alone would not say which direction. */
const ALERT_LABELS: Record<string, string> = Object.fromEntries(
    Object.entries(LABELS).map(([field, label]) => [
        field,
        field.startsWith("inbound") ? `Inbound: ${label}` : field.startsWith("outbound") ? `Outbound: ${label}` : label,
    ]),
);

const COLUMNS = [
    column<OrganizationItem>("code", "Code", "30%", row => row.code),
    column<OrganizationItem>("name", "Name", "35%", row => displayName(row)),
    column<OrganizationItem>("modified", "Modified", "35%", row => formatModified(row.stored)),
];

/**
 * Organizations in the Core configuration database (ADO 10076). Laid out like
 * the Mailboxes page: list on the left, editor on the right.
 *
 * The form is built from `stored` only. PUT replaces the whole stored row, so
 * a value taken from `effective` would silently become a database override.
 * The Core routes ignore the token's tenant claim; the selected tenant's token
 * is used because that is where a sys-admin token lives.
 */
function Organizations() {
    const { selectedTenant } = useContext(tenantContext);
    const token = selectedTenant?.token;

    const [items, setItems] = useState<OrganizationItem[]>([]);
    const [listState, setListState] = useState<"loading" | "loaded" | "failed">("loading");
    const [listError, setListError] = useState("");
    const [filter, setFilter] = useState("");
    const [selectedCode, setSelectedCode] = useState("");

    const [isNew, setIsNew] = useState<boolean | undefined>(undefined);
    const [item, setItem] = useState<OrganizationItem | undefined>(undefined);
    const [code, setCode] = useState("");
    const [form, setForm] = useState<FormState>({});
    const [secrets, setSecrets] = useState<SecretInputs>(EMPTY_SECRETS);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [savedJson, setSavedJson] = useState("");
    const [recordLoading, setRecordLoading] = useState(false);
    // The code of the latest open(), so a slow earlier response cannot overwrite a later one.
    const latestOpen = useRef("");

    useEffect(() => {
        loadList();
    }, [token]);

    async function loadList() {
        if (!token) {
            return;
        }
        setListState("loading");
        const result = await GetOrganizations(token);
        if (result.isOk && result.data) {
            setItems(result.data);
            setListState("loaded");
        } else {
            setListError(failureMessage(result.status));
            setListState("failed");
        }
    }

    function edit(next: OrganizationItem | undefined, newRecord: boolean) {
        const nextCode = next?.code ?? "";
        const nextForm = formFromSettings(ORGANIZATION_FIELDS, next?.stored);
        setItem(next);
        setCode(nextCode);
        setForm(nextForm);
        setSecrets(EMPTY_SECRETS);
        setErrors({});
        setSavedJson(JSON.stringify({ code: nextCode, form: nextForm, secrets: EMPTY_SECRETS }));
        setIsNew(newRecord);
    }

    function confirmDiscard(): boolean {
        const unsaved = isNew !== undefined && JSON.stringify({ code, form, secrets }) !== savedJson;
        return !unsaved || window.confirm("Discard your unsaved changes?");
    }

    async function open(rowCode: string) {
        if (!token || !confirmDiscard()) {
            return;
        }
        latestOpen.current = rowCode;
        setRecordLoading(true);
        const result = await GetOrganization(token, rowCode);
        if (latestOpen.current !== rowCode) {
            return;
        }
        setRecordLoading(false);
        if (result.isOk && result.data) {
            // Selected only now, so a failed open leaves the list matching the editor.
            setSelectedCode(rowCode);
            edit(result.data, false);
        } else if (result.status === 404) {
            alert(`${rowCode} no longer exists.`);
            setIsNew(undefined);
            setSelectedCode("");
            await loadList();
        } else {
            alert(failureMessage(result.status));
        }
    }

    function addNew() {
        if (!confirmDiscard()) {
            return;
        }
        latestOpen.current = "";
        setRecordLoading(false);
        setSelectedCode("");
        edit(undefined, true);
    }

    function updateValue(field: string, value: string) {
        if (field === "code") {
            setCode(value);
        } else if ((SECRET_NAMES as readonly string[]).includes(field)) {
            setSecrets(current => ({ ...current, [field]: value }));
        } else {
            setForm(current => ({ ...current, [field]: value }));
        }
    }

    async function save() {
        if (!token) {
            return;
        }
        const { body, errors: localErrors } = organizationRequest(form, secrets);
        if (isNew) {
            const problem = codeProblem(
                code,
                items.map(i => i.code),
            );
            if (problem) {
                localErrors.code = problem;
            }
        }
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            alert(errorSummary(localErrors, ALERT_LABELS));
            return;
        }

        const result = await PutOrganization(token, code, body);
        if (result.isOk && result.data) {
            edit(result.data, false);
            setSelectedCode(result.data.code);
            alert("success");
            await loadList();
        } else if (result.status === 400) {
            const serverErrors = fieldErrorsFromResponse(result);
            setErrors(serverErrors);
            const problem = result.validationErrors as unknown as { detail?: string; title?: string } | undefined;
            alert(
                errorSummary(serverErrors, ALERT_LABELS) ||
                    problem?.detail ||
                    problem?.title ||
                    "The server rejected the save.",
            );
        } else {
            alert(failureMessage(result.status));
        }
    }

    async function remove() {
        if (!token || !item || !window.confirm(deleteConfirmText(item.code))) {
            return;
        }
        const result = await DeleteOrganization(token, item.code);
        if (result.isOk) {
            setIsNew(undefined);
            setItem(undefined);
            setSelectedCode("");
            alert("deleted");
            await loadList();
        } else if (result.status === 409) {
            alert(
                `${result.conflict?.detail ?? "Tenants still link to this organization."}\n\nUnlink these tenants first.`,
            );
        } else {
            alert(failureMessage(result.status));
        }
    }

    async function removeSecret(name: SecretName) {
        if (!token || !item || !window.confirm(`Remove the stored secret "${ALERT_LABELS[name]}"?`)) {
            return;
        }
        const result = await DeleteOrganizationSecret(token, item.code, name);
        if (!result.isOk) {
            alert(failureMessage(result.status));
            return;
        }
        // Only the item is replaced, so the secret states refresh and other unsaved edits survive.
        const reread = await GetOrganization(token, item.code);
        if (reread.isOk && reread.data) {
            setItem(reread.data);
        } else {
            alert(failureMessage(reread.status));
        }
    }

    function copyEffective() {
        if (item?.effective) {
            setForm(formFromSettings(ORGANIZATION_FIELDS, item.effective));
            alert("Copied. Secrets are not copied: type them in before saving.");
        }
    }

    const stored = item?.stored;
    const effective = item?.effective;

    function textRow(path: string, suffix = "") {
        return (
            <div
                className="formRow"
                key={path}
            >
                <TextBox
                    id={path}
                    label={LABELS[path] + suffix}
                    value={form[path]}
                    onChange={updateValue}
                    maxLength={500}
                    errorMessage={errors[path] ?? ""}
                ></TextBox>
                <EffectiveValue
                    stored={stored}
                    effective={effective}
                    path={path}
                    error={errors[path]}
                />
            </div>
        );
    }

    function numberRow(path: string) {
        return (
            <div
                className="formRow"
                key={path}
            >
                <NumberBox
                    id={path}
                    label={LABELS[path]}
                    value={form[path]}
                    onChange={updateValue}
                    maxLength={10}
                    errorMessage={errors[path] ?? ""}
                ></NumberBox>
                <EffectiveValue
                    stored={stored}
                    effective={effective}
                    path={path}
                    error={errors[path]}
                />
            </div>
        );
    }

    function choiceRow(path: string, options: Option[]) {
        return (
            <div
                className="formRow"
                key={path}
            >
                <Dropdown
                    name={path}
                    label={LABELS[path]}
                    value={form[path] ?? ""}
                    returnFunc={updateValue}
                    Valid={!errors[path]}
                    options={options}
                ></Dropdown>
                <EffectiveValue
                    stored={stored}
                    effective={effective}
                    path={path}
                    error={errors[path]}
                />
            </div>
        );
    }

    function secretRow(name: SecretName, suffix = "") {
        const state = stored?.secrets?.[name];
        return (
            <div
                className="formRow"
                key={name}
            >
                <div className="cc-secret">
                    <TextBox
                        id={name}
                        label={LABELS[name] + suffix}
                        type="password"
                        placeholder={secretPlaceholder(state)}
                        value={secrets[name]}
                        onChange={updateValue}
                        maxLength={500}
                        errorMessage={errors[name] ?? ""}
                    ></TextBox>
                    {state?.isSet && (
                        <button
                            type="button"
                            className="g-btn btn-default"
                            onClick={() => removeSecret(name)}
                        >
                            Remove
                        </button>
                    )}
                </div>
                <EffectiveValue
                    stored={stored}
                    effective={effective}
                    path={`secrets.${name}.isSet`}
                    error={errors[name]}
                    secret
                />
            </div>
        );
    }

    function mailSection(direction: Direction) {
        const provider = form[`${direction}.mailProvider`] ?? "";
        const optional = credentialsOptional(provider) ? " (optional)" : "";
        return (
            <Section title={direction === "inbound" ? "Inbound mail" : "Outbound mail"}>
                {choiceRow(`${direction}.mailProvider`, choiceOptions(MAIL_PROVIDERS, provider))}
                {textRow(`${direction}.server`)}
                {numberRow(`${direction}.port`)}
                {textRow(`${direction}.username`, optional)}
                {direction === "outbound" &&
                    providerShows("authentication", provider) &&
                    choiceRow("outbound.authenticationMethods", AUTHENTICATION_OPTIONS)}
                {secretRow(`${direction}Password`, optional)}
                {providerShows("msal", provider) && (
                    <>
                        {textRow(`${direction}.msalTenantId`)}
                        {textRow(`${direction}.msalClientId`)}
                        {secretRow(`${direction}MsalClientSecret`)}
                    </>
                )}
            </Section>
        );
    }

    const rows = items.filter(row => matchesText(row, filter));

    return (
        <>
            <div className="topSection">
                <div className="sectionHeader">
                    {" "}
                    {listState === "loaded" && (
                        <PlusCircle
                            className="headerIcon"
                            aria-label="New organization"
                            onClick={addNew}
                        ></PlusCircle>
                    )}
                    Organizations{" "}
                </div>
                {isNew !== undefined && !recordLoading && (
                    <div>
                        {!isNew && stored && (
                            <button
                                className="g-btn-large btn-warning"
                                style={HEADER_BUTTON_STYLE}
                                onClick={remove}
                            >
                                Delete
                            </button>
                        )}
                        <button
                            className="g-btn-large btn-primary"
                            style={HEADER_BUTTON_STYLE}
                            onClick={save}
                        >
                            {isNew ? "Create Organization" : "Update Organization"}
                        </button>
                    </div>
                )}
            </div>
            <div style={{ display: "flex", flexDirection: "row", height: "calc(100% - 51px)" }}>
                <div className="Users-bodyCol1">
                    {listState === "loading" && <Loading></Loading>}
                    {listState === "failed" && <div className="cc-message">{listError}</div>}
                    {listState === "loaded" && (
                        <>
                            <div className="filterContainer">
                                <div>Filter: </div>
                                <div>
                                    <input
                                        type="text"
                                        aria-label="Filter organizations"
                                        style={{ width: "100%" }}
                                        onChange={e => setFilter(e.currentTarget.value)}
                                        value={filter}
                                    />
                                </div>
                            </div>
                            {rows.length === 0 && <div className="cc-message">No organizations.</div>}
                            <DataTable
                                data={rows}
                                columns={COLUMNS}
                                onRowClick={(val: string) => {
                                    open(val);
                                }}
                                onRowDblClick={() => {}}
                                selectedItem={selectedCode}
                                uniqueIdentifiyer={x => x.code}
                            ></DataTable>
                        </>
                    )}
                </div>
                {(isNew !== undefined || recordLoading) && (
                    <div className="bodyCol2">
                        {recordLoading && <Loading></Loading>}
                        {!recordLoading && isNew !== undefined && (
                            <>
                                <div className="optionsRow">
                                    <p style={EDITOR_TITLE_STYLE}>
                                        {isNew ? "Create Organization" : `Edit Organization ${item?.code}`}
                                    </p>
                                </div>
                                <div style={{ padding: "5px 0px 0px 5px" }}>
                                    {item && !effective && (
                                        <div className="cc-message">
                                            Saved in the database. This API host has not applied it yet.
                                        </div>
                                    )}
                                    {item && !stored && effective && (
                                        <div className="cc-message">
                                            No database values yet. Every value in use comes from appsettings.{" "}
                                            <button
                                                type="button"
                                                className="g-btn btn-default"
                                                onClick={copyEffective}
                                            >
                                                Copy effective values into the form
                                            </button>
                                        </div>
                                    )}
                                    {stored?.modifiedAt && (
                                        <div className="cc-message">Last saved {formatModified(stored)}</div>
                                    )}
                                    <Section title="General">
                                        <div className="formRow">
                                            <TextBox
                                                id="code"
                                                label="Code"
                                                value={code}
                                                onChange={updateValue}
                                                maxLength={100}
                                                disabled={!isNew}
                                                requiredIdentifier={isNew}
                                                errorMessage={errors.code ?? ""}
                                            ></TextBox>
                                            <EffectiveValue error={errors.code} />
                                        </div>
                                        {textRow("name")}
                                        {choiceRow("enableCloudMailProcessing", FLAG_OPTIONS)}
                                    </Section>
                                    <Section title="Auto-response">
                                        {numberRow("autoResponseMaxPerWindow")}
                                        {numberRow("autoResponseWindowMinutes")}
                                        {numberRow("autoResponseBodySizeToleranceBytes")}
                                    </Section>
                                    {mailSection("inbound")}
                                    {mailSection("outbound")}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default Organizations;
